import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Phone, RefreshCw, ArrowDownLeft, Filter } from 'lucide-react';
import api from '../lib/api';

export const SmsPhoneCard = () => {
  const [messages, setMessages] = useState([]);
  const [senders, setSenders] = useState([]);
  const [selectedSender, setSelectedSender] = useState('');
  const [loading, setLoading] = useState(true);
  const [twilioPhone, setTwilioPhone] = useState('');
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const fetchMessages = async (sender) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (sender) params.set('sender', sender);
      const { data } = await api.get(`/sms/messages?${params}`);
      setMessages(data.messages || []);
      if (!sender && data.senders) setSenders(data.senders);
      setTwilioPhone(data.twilio_phone || '');
      setError(data.error || null);
    } catch {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(''); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSenderChange = (val) => {
    setSelectedSender(val);
    fetchMessages(val);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  const sorted = [...messages].reverse();

  return (
    <div
      data-testid="sms-phone-card"
      className="border border-border bg-card flex flex-col"
      style={{ width: 360, height: 560, borderRadius: 28, overflow: 'hidden' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3" style={{ background: '#18181b' }}>
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-semibold text-zinc-100 tracking-wide">
            {twilioPhone || 'Inbox'}
          </span>
        </div>
        <button
          onClick={() => fetchMessages(selectedSender)}
          disabled={loading}
          className="p-1 rounded-full hover:bg-zinc-700 transition-colors"
          data-testid="sms-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sender filter */}
      <div className="flex items-center gap-2 px-4 py-2" style={{ background: '#1a1a1e' }}>
        <Filter className="h-3 w-3 text-zinc-500 shrink-0" />
        <select
          data-testid="sms-sender-filter"
          value={selectedSender}
          onChange={(e) => handleSenderChange(e.target.value)}
          className="flex-1 text-[11px] bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 outline-none focus:border-emerald-500 transition-colors"
        >
          <option value="">All senders</option>
          {senders.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Status bar */}
      <div
        className="flex items-center justify-center py-1 text-[10px] text-zinc-500 font-medium tracking-wider"
        style={{ background: '#18181b' }}
      >
        <MessageSquare className="h-3 w-3 mr-1 text-zinc-600" />
        {messages.length} received message{messages.length !== 1 ? 's' : ''}
      </div>

      {/* Message area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
        style={{ background: '#09090b' }}
      >
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="h-6 w-6 animate-spin text-zinc-600" />
          </div>
        ) : error && messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-xs">
            <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
            <p>{error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-xs">
            <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
            <p>No received messages</p>
          </div>
        ) : (
          sorted.map((msg) => (
            <div key={msg.sid} className="flex flex-col items-start" data-testid={`sms-bubble-${msg.sid}`}>
              {/* Sender */}
              <div className="flex items-center gap-1 mb-0.5 px-1">
                <ArrowDownLeft className="h-2.5 w-2.5 text-emerald-400" />
                <span className="text-[11px] text-emerald-400 font-semibold tracking-wide">
                  {msg.from}
                </span>
              </div>

              {/* Bubble */}
              <div
                className="max-w-[280px] px-3 py-2 text-[13px] leading-relaxed bg-zinc-800 text-zinc-100"
                style={{ borderRadius: '16px 16px 16px 4px' }}
              >
                {msg.body}
              </div>

              {/* Timestamp */}
              <span className="text-[9px] text-zinc-600 mt-0.5 px-1">
                {formatTime(msg.date_sent || msg.date_created)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Bottom bar */}
      <div className="px-4 py-2 flex items-center justify-between" style={{ background: '#18181b' }}>
        <span className="text-[10px] text-zinc-600">Powered by Twilio</span>
        <span className="text-[10px] text-zinc-600">
          {messages.length > 0 && formatTime(messages[0]?.date_sent || messages[0]?.date_created)}
        </span>
      </div>
    </div>
  );
};
